import { useRef, useCallback, useState } from 'react';
import { criarFeedback, atualizarFeedback } from '../api';
import { formatBrasilia } from '../../../shared/lib/date';
import type { CreateFeedbackPayload } from '../api';

type DraftFields = Omit<CreateFeedbackPayload, 'managerId'>;
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function useFeedbackDraft(initialDraftId?: string | null) {
  const draftIdRef = useRef<string | null>(initialDraftId ?? null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const scheduleSave = useCallback((fields: DraftFields) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSaveStatus('saving');

    timerRef.current = setTimeout(async () => {
      try {
        if (!draftIdRef.current) {
          // Primeiro save: cria o rascunho (o backend pega managerId do token)
          const created = await criarFeedback(fields as CreateFeedbackPayload);
          draftIdRef.current = created.id;
        } else {
          await atualizarFeedback(draftIdRef.current, fields);
        }
        setSaveStatus('saved');
        setSavedAt(formatBrasilia(new Date()));
      } catch {
        setSaveStatus('error');
      }
    }, 2000);
  }, []);

  const getDraftId = () => draftIdRef.current;

  const resetDraft = useCallback(() => {
    draftIdRef.current = null;
    setSaveStatus('idle');
    setSavedAt(null);
  }, []);

  return { scheduleSave, getDraftId, saveStatus, savedAt, resetDraft };
}
